import { Component, Input, OnChanges, OnInit, SimpleChanges, inject, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { WishesService } from './wishes.service';

type DaySummary = { entries: number };

export type WishTrackCard = {
  wishName: string | null;
  wishDescription: string | null;
  actionLabel: string | null;
  status: string | null;
};

@Component({
  selector: 'app-wishes-track',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDialogModule,
  ],
  templateUrl: './wishes-track.component.html',
  styleUrl: './wishes-track.component.css',
})
export class WishesTrackComponent implements OnInit, OnChanges {
  @Input() memberId: string = '';
  readonly dialog = inject(MatDialog);

  selectedMonth: number = new Date().getMonth();
  selectedYear: number = new Date().getFullYear();

  calendarDays: Array<{
    day: number | null;
    date: Date | null;
    dateKey?: string;
    isCurrentMonth: boolean;
    entries?: number;
  }> = [];

  months: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  years: number[] = [];

  isLoading = false;
  private monthViewItems: any[] = [];
  private dayMap: Record<string, DaySummary> = {};

  constructor(private wishesService: WishesService) {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      this.years.push(i);
    }
  }

  ngOnInit(): void {
    this.clampMonthYearToToday();
    this.loadMonthView();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['memberId'] && !changes['memberId'].firstChange) {
      this.loadMonthView();
    }
  }

  onMonthYearChange(): void {
    this.clampMonthYearToToday();
    this.loadMonthView();
  }

  isMonthDisabled(monthIndex: number): boolean {
    const now = new Date();
    if (this.selectedYear === now.getFullYear()) return monthIndex > now.getMonth();
    return false;
  }

  isYearDisabled(year: number): boolean {
    return year > new Date().getFullYear();
  }

  onDayClick(day: {
    day: number | null;
    date: Date | null;
    dateKey?: string;
    isCurrentMonth: boolean;
    entries?: number;
  }): void {
    if (!day?.isCurrentMonth) return;
    const entries = Number(day.entries ?? 0);
    if (!entries || entries <= 0) return;
    const dateKey = (day.dateKey ?? '') as string;
    if (!dateKey) return;

    const apiDay = this.monthViewItems.find((x) => (this.extractDateKey(x) ?? '') === dateKey);
    const cards = this.buildTrackCardsForDay(apiDay);

    const dateObj = new Date(`${dateKey}T00:00:00`);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[dateObj.getDay()];

    this.dialog.open(WishesTrackDetailDialogComponent, {
      width: '92%',
      maxWidth: '1350px',
      hasBackdrop: true,
      backdropClass: 'wishes-track-dialog-backdrop',
      disableClose: false,
      data: {
        dayName,
        date: dateKey,
        cards,
      },
    });
  }

  private clampMonthYearToToday(): void {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    if (this.selectedYear > currentYear) this.selectedYear = currentYear;
    if (this.selectedYear === currentYear && this.selectedMonth > currentMonth) {
      this.selectedMonth = currentMonth;
    }
  }

  private loadMonthView(): void {
    this.dayMap = {};
    this.monthViewItems = [];

    const mid = (this.memberId ?? '').toString().trim();
    if (!mid) {
      this.generateCalendar();
      return;
    }

    const apiMonth = this.selectedMonth + 1;
    const apiYear = this.selectedYear;

    this.isLoading = true;
    this.wishesService.getMemberWishesMonthView(mid, apiMonth, apiYear).subscribe({
      next: (res) => {
        this.processMonthView(res);
        this.generateCalendar();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching wishes month view:', err);
        this.dayMap = {};
        this.generateCalendar();
        this.isLoading = false;
      },
    });
  }

  private processMonthView(data: any): void {
    const items = this.normalizeMonthViewItems(data);
    this.monthViewItems = items;

    items.forEach((item) => {
      const dateKey = this.extractDateKey(item);
      if (!dateKey) return;
      if (!this.dayMap[dateKey]) this.dayMap[dateKey] = { entries: 0 };
      this.dayMap[dateKey].entries += this.countDayEntries(item);
    });
  }

  private normalizeMonthViewItems(data: any): any[] {
    const normalized = data?.result ?? data?.data ?? data;
    if (Array.isArray(normalized)) return normalized;
    if (Array.isArray(normalized?.days)) return normalized.days;
    if (Array.isArray(normalized?.items)) return normalized.items;
    if (normalized && typeof normalized === 'object') return [normalized];
    return [];
  }

  private countDayEntries(item: any): number {
    if (typeof item?.entries === 'number' && Number.isFinite(item.entries)) {
      return item.entries;
    }
    if (typeof item?.entryCount === 'number' && Number.isFinite(item.entryCount)) {
      return item.entryCount;
    }

    const wishes = this.extractWishesForDay(item);
    if (wishes.length === 0) return 0;

    return wishes.reduce((acc, wish) => acc + this.countWishEntries(wish), 0);
  }

  private extractWishesForDay(item: any): any[] {
    const candidates = [item?.wishes, item?.wishEntries, item?.trackedWishes, item?.items];
    for (const c of candidates) {
      if (Array.isArray(c)) return c;
    }
    return [];
  }

  private countWishEntries(wish: any): number {
    const actions = Array.isArray(wish?.actions) ? wish.actions : [];
    if (actions.length > 0) return actions.length;
    return 1;
  }

  private buildTrackCardsForDay(apiDay: any | undefined): WishTrackCard[] {
    if (!apiDay) return [];
    const wishes = this.extractWishesForDay(apiDay);
    const cards: WishTrackCard[] = [];

    wishes.forEach((wish) => {
      const wishName = (wish?.wishName ?? wish?.name ?? null) as string | null;
      const wishDescription = (wish?.wishDescription ?? wish?.description ?? null) as string | null;
      const actions = Array.isArray(wish?.actions) ? wish.actions : [];

      if (actions.length === 0) {
        cards.push({
          wishName,
          wishDescription,
          actionLabel: null,
          status: (wish?.status ?? null) as string | null,
        });
        return;
      }

      actions.forEach((action: any) => {
        cards.push({
          wishName,
          wishDescription,
          actionLabel: this.formatActionLabel(action),
          status: (action?.status ?? action?.completionStatus ?? wish?.status ?? null) as string | null,
        });
      });
    });

    return cards;
  }

  private formatActionLabel(action: any): string {
    if (action?.goal) {
      const pillar = action?.goalPillar ? `${action.goalPillar} - ` : '';
      return `${pillar}${action.goal}`;
    }
    if (action?.title) return String(action.title);
    if (action?.moodCheckInName) return String(action.moodCheckInName);
    if (action?.habitName) return String(action.habitName);
    return '';
  }

  private extractDateKey(item: any): string | null {
    if (item?.dateKey && typeof item.dateKey === 'string') {
      const v = item.dateKey.trim();
      return v ? v : null;
    }
    if (item?.date) {
      if (typeof item.date === 'string' && item.date.trim()) return item.date.trim();
      try {
        const d = new Date(item.date);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      } catch {
        return null;
      }
    }
    return null;
  }

  private generateCalendar(): void {
    this.calendarDays = [];
    const firstDay = new Date(this.selectedYear, this.selectedMonth, 1);
    const lastDay = new Date(this.selectedYear, this.selectedMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    for (let i = 0; i < startingDayOfWeek; i++) {
      this.calendarDays.push({ day: null, date: null, isCurrentMonth: false });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(this.selectedYear, this.selectedMonth, day);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const dateKey = `${y}-${m}-${d}`;
      const summary = this.dayMap[dateKey] ?? { entries: 0 };

      this.calendarDays.push({
        day,
        date,
        dateKey,
        isCurrentMonth: true,
        entries: summary.entries ?? 0,
      });
    }

    const totalCells = this.calendarDays.length;
    const remainingCells = 7 - (totalCells % 7);
    if (remainingCells < 7) {
      for (let i = 0; i < remainingCells; i++) {
        this.calendarDays.push({ day: null, date: null, isCurrentMonth: false });
      }
    }
  }
}

@Component({
  selector: 'app-wishes-track-detail-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './wishes-track-detail-dialog.component.html',
  styleUrl: './wishes-track-detail-dialog.component.css',
})
export class WishesTrackDetailDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<WishesTrackDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { dayName: string; date: string; cards: WishTrackCard[] }
  ) {}

  closeDialog(): void {
    this.dialogRef.close();
  }
}
